import axios from "axios";
import { getAuthHeader } from "./AuthContext";
import { cacheGet, cacheGetSync, cacheSet, cacheMutate, enqueueMutation } from "./offlineStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Which GET-URL prefiks skal cachast for offline-visning
const OFFLINE_GET_PREFIXES = [
  "/pantry",
  "/shopping-lists",
  "/boats/active",
  "/boats",
  "/storage-locations",
  "/auth/me",
  "/trips/active",
  "/withdrawals",
];
// Kva URL-prefiksar der POST/PUT/DELETE skal kø-lagrast offline
const OFFLINE_MUTATION_PREFIXES = ["/pantry", "/shopping-lists"];

function stripApi(url) {
  if (!url) return "";
  return url.startsWith(API) ? url.slice(API.length) : url;
}

function shouldCacheGet(url) {
  const p = stripApi(url);
  return OFFLINE_GET_PREFIXES.some((pref) => p.startsWith(pref));
}

function shouldQueueMutation(url) {
  const p = stripApi(url);
  return OFFLINE_MUTATION_PREFIXES.some((pref) => p.startsWith(pref));
}

/**
 * Optimistisk oppdatering av cache når ein pantry- eller shopping-mutasjon
 * blir kølagt offline. Slik ser brukaren sine eigne endringar umiddelbart
 * i lista, sjølv om det ekte kallet skjer først når nettet er tilbake.
 */
async function applyOptimisticCache(method, path, payload) {
  const m = method.toLowerCase();
  // ── PANTRY ────────────────────────────────────────────────────────
  // POST /pantry — opprett ny vare
  if (m === "post" && path === "/pantry") {
    const tmpId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const nowIso = new Date().toISOString();
    const optimistic = {
      id: tmpId,
      __offline: true,
      name: "",
      amount: "",
      unit: "",
      category: "",
      location_id: "",
      in_stock: true,
      created_at: nowIso,
      ...payload,
    };
    await cacheMutate("/pantry", (data) => {
      if (!Array.isArray(data)) return undefined;
      return [optimistic, ...data];
    });
    return;
  }
  // PUT /pantry/{id} — oppdater vare
  const pantryPutMatch = m === "put" && path.match(/^\/pantry\/([^/]+)$/);
  if (pantryPutMatch) {
    const id = pantryPutMatch[1];
    await cacheMutate("/pantry", (data) => {
      if (!Array.isArray(data)) return undefined;
      return data.map((it) => (it.id === id ? { ...it, ...payload, __offline: true } : it));
    });
    return;
  }
  // DELETE /pantry/{id} — slett vare
  const pantryDelMatch = m === "delete" && path.match(/^\/pantry\/([^/]+)$/);
  if (pantryDelMatch) {
    const id = pantryDelMatch[1];
    await cacheMutate("/pantry", (data) => {
      if (!Array.isArray(data)) return undefined;
      return data.filter((it) => it.id !== id);
    });
    return;
  }
  // ── SHOPPING-LISTS ────────────────────────────────────────────────
  // PUT /shopping-lists/{weekStart}?list_id=&shift= — full replace
  const shopPutMatch = m === "put" && path.match(/^\/shopping-lists\/([^/?]+)(\?.*)?$/);
  if (shopPutMatch && payload && typeof payload === "object") {
    // Cache-scope inkluderer heile stien inkl. query-params.
    // Vi lagrar tilbake med same payload-form som serveren ville returnert
    // — dvs. { items: [...] } eller heile listeobjektet.
    await cacheMutate(path, (data) => {
      if (!data || typeof data !== "object") return undefined;
      // Serveren returnerer typisk { items, total, ... }. Vi merge-oppdaterer.
      return { ...data, ...payload, __offline: true };
    });
    return;
  }
}

// All requests include cookies for auth
axios.defaults.withCredentials = true;

// Add Authorization header to every request as fallback for cookies
axios.interceptors.request.use((config) => {
  const authHeader = getAuthHeader();
  if (authHeader.Authorization) {
    config.headers.Authorization = authHeader.Authorization;
  }
  return config;
});

// Auto-logout on 401 (token expired/invalid)
let hasShown401 = false;
axios.interceptors.response.use(
  (res) => {
    // Cache GET-responsar for offline visning
    try {
      const method = (res.config?.method || "get").toLowerCase();
      if (method === "get" && shouldCacheGet(res.config?.url)) {
        cacheSet(stripApi(res.config.url), res.data);
      }
    } catch { /* ignore */ }
    return res;
  },
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url || "";
    const method = (err.config?.method || "get").toLowerCase();

    // Offline-håndtering: nettverksfeil (ingen response) på pantry/shopping URLs
    if (!err.response && err.config) {
      // GET: prøv cache — bruk sync-shadow først (rask), fall back til async IDB read
      if (method === "get" && shouldCacheGet(url)) {
        const scope = stripApi(url);
        let cached = cacheGetSync(scope);
        if (cached) {
          console.info("[offline] serving cache (sync) for", url);
          return Promise.resolve({ data: cached.data, status: 200, config: err.config, headers: {}, cached: true });
        }
        // Sjekk asynkront (IDB) som fallback
        return cacheGet(scope).then((c) => {
          if (c) {
            console.info("[offline] serving cache (async) for", url);
            return { data: c.data, status: 200, config: err.config, headers: {}, cached: true };
          }
          return Promise.reject(err);
        });
      }
      // POST/PUT/DELETE på pantry/shopping: kø for seinare
      if (["post", "put", "patch", "delete"].includes(method) && shouldQueueMutation(url)) {
        let payload = err.config.data;
        try { if (typeof payload === "string") payload = JSON.parse(payload); } catch { /* keep as string */ }
        enqueueMutation({
          method: method.toUpperCase(),
          url: stripApi(url),
          data: payload,
          description: `${method.toUpperCase()} ${stripApi(url)}`,
        });
        // Oppdater cache optimistisk så brukaren ser eiga endring med ein gong
        applyOptimisticCache(method, stripApi(url), typeof payload === "object" ? payload : {}).catch(() => { /* ignore */ });
        console.info("[offline] queued mutation", method, url);
        return Promise.resolve({ data: { queued: true, ...(typeof payload === "object" ? payload : {}) }, status: 202, config: err.config, headers: {}, queued: true });
      }
    }

    // Ignore /auth/me which is part of boot auth check (expected 401 when logged out)
    if (status === 401 && !url.includes("/auth/me") && !url.includes("/auth/login") && !url.includes("/auth/register")) {
      if (!hasShown401) {
        hasShown401 = true;
        localStorage.removeItem("access_token");
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          // Show toast if available
          try {
            // Lazy import toast to avoid circular dep
            import("sonner").then(({ toast }) => toast.error("Sesjon utløpt — logg inn på nytt"));
          } catch (e) { /* ignore */ }
          setTimeout(() => {
            window.location.href = "/login";
          }, 1200);
        }
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  // Recipes
  getRecipes: (isBaking, search) => {
    const params = {};
    if (isBaking !== undefined) params.is_baking = isBaking;
    if (search) params.search = search;
    return axios.get(`${API}/recipes`, { params });
  },
  getRecipe: (id) => axios.get(`${API}/recipes/${id}`),
  createRecipe: (data) => axios.post(`${API}/recipes`, data),
  deleteRecipe: (id) => axios.delete(`${API}/recipes/${id}`),
  updateRecipe: (id, data) => axios.put(`${API}/recipes/${id}`, data),
  calculateNutrition: (id) => axios.post(`${API}/recipes/${id}/nutrition`),
  toggleFavorite: (id) => axios.put(`${API}/recipes/${id}/favorite`),
  regenerateRecipeImage: (id) => axios.post(`${API}/recipes/${id}/regenerate-image`, null, { timeout: 90000 }),
  autoCategorizeRecipes: () => axios.post(`${API}/recipes/auto-categorize`),
  findRecipeDuplicates: () => axios.post(`${API}/recipes/find-duplicates`, {}, { timeout: 180000 }),
  deleteRecipeDuplicates: (ids) => axios.post(`${API}/recipes/delete-duplicates`, { ids }),
  getRecipeBoatNames: () => axios.get(`${API}/recipes-meta/boat-names`),
  uploadRecipeImage: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/recipes/${id}/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  generateRecipe: (data) => axios.post(`${API}/recipes/generate`, data),
  importPdfRecipe: (formData) => axios.post(`${API}/recipes/import-pdf`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  importRecipeFromImages: (formData) => axios.post(`${API}/recipes/import-images`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  importUrlRecipe: (data) => axios.post(`${API}/recipes/import-url`, data),
  bulkCreateRecipes: (recipes) => axios.post(`${API}/recipes/bulk`, recipes),

  // Meal Plans
  getMealPlan: (weekStart, shift = "1") => axios.get(`${API}/meal-plans/${weekStart}`, { params: { shift } }),
  updateMealPlan: (weekStart, data, shift = "1") => axios.put(`${API}/meal-plans/${weekStart}`, data, { params: { shift } }),
  suggestMenu: (data) => axios.post(`${API}/meal-plans/suggest`, data),
  aiGenerateWeekPlan: (weekStart, data) => axios.post(`${API}/meal-plans/${weekStart}/ai-generate`, data || {}),
  getMealWishes: () => axios.get(`${API}/meal-wishes`),
  addMealWish: (text) => axios.post(`${API}/meal-wishes`, { text }),
  deleteMealWish: (id) => axios.delete(`${API}/meal-wishes/${id}`),
  resetMealPlans: (params) => axios.delete(`${API}/meal-plans/reset`, { params }),

  // Baking Plans
  getBakingPlan: (weekStart) => axios.get(`${API}/baking-plans/${weekStart}`),
  updateBakingPlan: (weekStart, data) => axios.put(`${API}/baking-plans/${weekStart}`, data),
  generateBakingImage: (weekStart, itemId) => axios.post(`${API}/baking-plans/${weekStart}/items/${itemId}/generate-image`),

  // Shopping Lists — `shift` is REQUIRED for main list (per-shift); custom
  // lists ignore it. Default to "1" but callers should pass the user's
  // active shift.
  getShoppingList: (weekStart, listId, shift = "1") =>
    axios.get(`${API}/shopping-lists/${weekStart}`, { params: { list_id: listId || "", shift } }),
  updateShoppingList: (weekStart, data, listId, shift = "1") =>
    axios.put(`${API}/shopping-lists/${weekStart}`, data, { params: { list_id: listId || "", shift } }),
  listAllShoppingLists: (shift = "") =>
    axios.get(`${API}/shopping-lists`, { params: shift ? { shift } : {} }),
  clearShoppingList: (weekStart, listId = "main", shift = "1") =>
    axios.delete(`${API}/shopping-lists/${weekStart}/items`, { params: { list_id: listId, shift } }),
  generateShoppingList: (weekStart, options = {}) =>
    axios.post(`${API}/shopping-lists/${weekStart}/generate`, options),
  generateAIShoppingList: (weekStart, shift = "1") =>
    axios.post(`${API}/shopping-lists/${weekStart}/generate-ai`, null, { params: { shift } }),
  estimateAIItemPrices: (weekStart, shift = "1") =>
    axios.post(`${API}/shopping-lists/${weekStart}/ai-items/estimate-prices`, null, { params: { shift } }),
  moveAIItemsToMain: (weekStart, shift = "1") =>
    axios.post(`${API}/shopping-lists/${weekStart}/ai-items/move-to-main`, null, { params: { shift } }),
  livePricesShopping: (weekStart, shift = "1") =>
    axios.post(`${API}/shopping-lists/${weekStart}/live-prices`, null, { params: { shift } }),
  checkoutShopping: (weekStart, data, shift = "1") =>
    axios.post(`${API}/shopping-lists/${weekStart}/checkout`, data || {}, { params: { shift } }),
  getShoppingDuplicates: (weekStart, shift = "1", listId = "") =>
    axios.get(`${API}/shopping-lists/${weekStart}/duplicates`, { params: { shift, list_id: listId } }),
  mergeShoppingDuplicates: (weekStart, data, shift = "1", listId = "") =>
    axios.post(`${API}/shopping-lists/${weekStart}/merge-duplicates`, data || {}, { params: { shift, list_id: listId } }),

  // Stores (fixed list)
  getStores: () => axios.get(`${API}/stores`),

  // Custom Lists
  getCustomLists: () => axios.get(`${API}/custom-lists`),
  createCustomList: (data) => axios.post(`${API}/custom-lists`, data),
  updateCustomList: (id, data) => axios.put(`${API}/custom-lists/${id}`, data),
  deleteCustomList: (id) => axios.delete(`${API}/custom-lists/${id}`),

  // Pantry
  getStorageLocations: () => axios.get(`${API}/storage-locations`),
  createStorageLocation: (data) => axios.post(`${API}/storage-locations`, data),
  deleteStorageLocation: (id) => axios.delete(`${API}/storage-locations/${id}`),
  getPantry: (locationId) => {
    const params = locationId ? { location_id: locationId } : {};
    return axios.get(`${API}/pantry`, { params });
  },
  createPantryItem: (data) => axios.post(`${API}/pantry`, data),
  updatePantryItem: (id, data) => axios.put(`${API}/pantry/${id}`, data),
  deletePantryItem: (id) => axios.delete(`${API}/pantry/${id}`),
  getExpiringItems: () => axios.get(`${API}/notifications/expiring`),
  autoAddToPantry: (data) => axios.post(`${API}/shopping-lists/auto-pantry`, data),
  getWasteReport: () => axios.get(`${API}/reports/waste`),
  getWithdrawals: () => axios.get(`${API}/withdrawals`),
  createWithdrawal: (data) => axios.post(`${API}/withdrawals`, data),

  // Boats
  getBoats: () => axios.get(`${API}/boats`),
  updateBoatRecipeCategories: (boatId, categories) => axios.put(`${API}/boats/${boatId}/recipe-categories`, { recipe_categories: categories }),
  createBoat: (data) => axios.post(`${API}/boats`, data),
  getBudgetStatus: () => axios.get(`${API}/budget/status`),
  updateBoat: (id, data) => axios.put(`${API}/boats/${id}`, data),
  deleteBoat: (id) => axios.delete(`${API}/boats/${id}`),
  activateBoat: (id) => axios.put(`${API}/boats/${id}/activate`),
  updateShiftConfig: (id, data) => axios.put(`${API}/boats/${id}/shift-config`, data),
  updateExtraSlots: (id, slots) => axios.put(`${API}/boats/${id}/extra-slots`, { extra_slots: slots }),
  getAdminHealth: () => axios.get(`${API}/admin/health`),
  getAdminOverview: () => axios.get(`${API}/admin/overview`),
  renameItemEverywhere: (oldName, newName) => axios.post(`${API}/items/rename`, { old_name: oldName, new_name: newName }),
  // Todos
  // Calendar (Home Edition)
  listCalendarEvents: (start, end) => axios.get(`${API}/calendar/events`, { params: { start, end } }),
  createCalendarEvent: (data) => axios.post(`${API}/calendar/events`, data),
  updateCalendarEvent: (id, data) => axios.put(`${API}/calendar/events/${id}`, data),
  deleteCalendarEvent: (id) => axios.delete(`${API}/calendar/events/${id}`),
  importCalendarIcs: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return axios.post(`${API}/calendar/import-ics`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  importCalendarIcsUrl: (url) => axios.post(`${API}/calendar/import-ics-url`, { url }, { timeout: 30000 }),
  // Calendar subscriptions (multi, auto-synced daily)
  listCalendarSubscriptions: () => axios.get(`${API}/calendar/subscriptions`),
  createCalendarSubscription: (name, url) => axios.post(`${API}/calendar/subscriptions`, { name, url }, { timeout: 30000 }),
  syncCalendarSubscription: (id) => axios.post(`${API}/calendar/subscriptions/${id}/sync`, null, { timeout: 30000 }),
  deleteCalendarSubscription: (id) => axios.delete(`${API}/calendar/subscriptions/${id}`),
  // Household .ics feed (public with secret token)
  getCalendarFeedInfo: () => axios.get(`${API}/calendar/feed-info`),
  rotateCalendarFeedToken: () => axios.post(`${API}/calendar/feed-info/rotate`),
  // Per-event single-ics download (so phone's native calendar can add it directly)
  calendarEventIcsUrl: (eventId) => `${API}/calendar/events/${eventId}/download.ics`,
  // Apple iCloud two-way sync
  getIcloudStatus: () => axios.get(`${API}/integrations/icloud/status`),
  connectIcloud: (apple_id, app_password) => axios.post(`${API}/integrations/icloud/connect`, { apple_id, app_password }, { timeout: 60000 }),
  syncIcloudNow: () => axios.post(`${API}/integrations/icloud/sync-now`, null, { timeout: 120000 }),
  disconnectIcloud: () => axios.delete(`${API}/integrations/icloud`),
  // Google Calendar
  getGoogleCalStatus: () => axios.get(`${API}/integrations/google/status`),
  getGoogleAuthorizeUrl: () => axios.get(`${API}/integrations/google/authorize-url`),
  syncGoogleCalNow: () => axios.post(`${API}/integrations/google/sync-now`, null, { timeout: 120000 }),
  disconnectGoogleCal: () => axios.delete(`${API}/integrations/google`),
  // Microsoft Outlook
  getOutlookStatus: () => axios.get(`${API}/integrations/outlook/status`),
  getOutlookAuthorizeUrl: () => axios.get(`${API}/integrations/outlook/authorize-url`),
  syncOutlookNow: () => axios.post(`${API}/integrations/outlook/sync-now`, null, { timeout: 120000 }),
  disconnectOutlook: () => axios.delete(`${API}/integrations/outlook`),
  // AI staveretter (Norwegian spellcheck)
  spellCheck: (text, context = "generell") => axios.post(`${API}/ai/spell-check`, { text, context }, { timeout: 30000 }),
  // Mark a recipe as cooked — registers pantry withdrawals for matching ingredients
  consumeRecipe: (recipeId, scale = 1) => axios.post(`${API}/recipes/${recipeId}/consume`, { scale }),
  // Web Push (Home Edition only)
  getVapidPublicKey: () => axios.get(`${API}/push/vapid-public-key`),
  pushSubscribe: (data) => axios.post(`${API}/push/subscribe`, data),
  pushUnsubscribe: (data) => axios.delete(`${API}/push/subscribe`, { data }),
  pushStatus: () => axios.get(`${API}/push/status`),
  sendTestPush: () => axios.post(`${API}/push/test`),
  listPushDevices: () => axios.get(`${API}/push/devices`),
  testPushDevice: (endpoint) => axios.post(`${API}/push/devices/test`, { endpoint }),
  deletePushDevice: (endpoint) => axios.delete(`${API}/push/devices`, { data: { endpoint } }),
  // In-app notifications feed (Home Edition)
  listNotifications: (limit = 30) => axios.get(`${API}/notifications`, { params: { limit } }),
  markNotificationRead: (id) => axios.post(`${API}/notifications/${id}/read`),
  markAllNotificationsRead: () => axios.post(`${API}/notifications/read-all`),
  deleteNotification: (id) => axios.delete(`${API}/notifications/${id}`),
  clearReadNotifications: () => axios.delete(`${API}/notifications`),
  getNotificationPreferences: () => axios.get(`${API}/notification-preferences`),
  updateNotificationPreferences: (prefs) => axios.put(`${API}/notification-preferences`, { prefs }),

  getTodos: () => axios.get(`${API}/todos`),
  createTodo: (data) => axios.post(`${API}/todos`, typeof data === "string" ? { text: data } : data),
  updateTodo: (id, data) => axios.put(`${API}/todos/${id}`, data),
  deleteTodo: (id) => axios.delete(`${API}/todos/${id}`),
  clearDoneTodos: () => axios.delete(`${API}/todos`),
  // Kassal prices
  kassalStatus: () => axios.get(`${API}/kassal/status`),
  kassalSearch: (q, size = 10, store) => axios.get(`${API}/kassal/search`, { params: { q, size, ...(store ? { store } : {}) } }),
  kassalCompare: (names) => axios.post(`${API}/kassal/compare`, { names }),
  kassalQuickPrice: (name, store) => axios.post(`${API}/kassal/quick-price`, { name, store: store || null }),
  shoppingCheckout: (weekStart, store, shift = "1") => axios.post(`${API}/shopping-lists/${weekStart}/checkout`, { store: store || null }, { params: { shift } }),
  shoppingListLivePrices: (weekStart, shift = "1") => axios.post(`${API}/shopping-lists/${weekStart}/live-prices`, null, { params: { shift } }),
  // Weather (MET.no via backend proxy)
  getWeather: (params = {}) => axios.get(`${API}/weather`, { params }),
  // Admin household CRUD
  createHousehold: (data) => axios.post(`${API}/admin/households`, data),
  deleteHousehold: (id) => axios.delete(`${API}/admin/households/${id}`),
  // Admin: global user moderation
  listAdminUsers: () => axios.get(`${API}/admin/users`),
  deleteAdminUser: (userId) => axios.delete(`${API}/admin/users/${userId}`),
  bulkDeleteAdminUsers: (userIds) => axios.post(`${API}/admin/users/bulk-delete`, { user_ids: userIds }),
  // Admin: edition access management
  setUserEditions: (userId, editions) => axios.put(`${API}/admin/users/${userId}/editions`, { editions }),
  listEditionRequests: (status = "pending") => axios.get(`${API}/admin/edition-requests`, { params: { status } }),
  decideEditionRequest: (reqId, decision) => axios.post(`${API}/admin/edition-requests/${reqId}/decide`, { decision }),
  // User-side
  requestEditionAccess: (edition) => axios.post(`${API}/auth/request-edition-access`, { edition }),
  setPreferredEdition: (edition) => axios.put(`${API}/auth/preferred-edition`, { edition }),
  // Messages
  getMessages: (since) => axios.get(`${API}/messages`, { params: since ? { since } : {} }),
  postMessage: (text) => axios.post(`${API}/messages`, { text }),
  deleteMessage: (id) => axios.delete(`${API}/messages/${id}`),
  // Member roles — see updateMemberRole further down in this file (deduped)
  // Product catalog
  getCatalog: (params) => axios.get(`${API}/catalog`, { params }),
  createCatalogEntry: (data) => axios.post(`${API}/catalog`, data),
  getCatalogSuggestions: (limit = 20) => axios.get(`${API}/catalog/suggestions`, { params: { limit } }),
  catalogAddToShopping: (id) => axios.post(`${API}/catalog/${encodeURIComponent(id)}/add-to-shopping`),
  catalogAddToPantry: (id) => axios.post(`${API}/catalog/${encodeURIComponent(id)}/add-to-pantry`),
  deleteCatalogEntry: (id) => axios.delete(`${API}/catalog/${encodeURIComponent(id)}`),
  updateCatalogEntry: (id, data) => axios.put(`${API}/catalog/${encodeURIComponent(id)}`, data),
  findCatalogDuplicates: () => axios.get(`${API}/catalog/duplicates`),
  mergeCatalogEntries: (keepId, mergeIds) => axios.post(`${API}/catalog/merge`, { keep_id: keepId, merge_ids: mergeIds }),
  updateBoatDeleteCode: (id, code) => axios.put(`${API}/boats/${id}/delete-code`, { code }),
  updateBoatCategoryOrder: (id, order) => axios.put(`${API}/boats/${id}/category-order`, { category_order: order }),
  updateBoatCategorySubgroups: (id, subgroups) => axios.put(`${API}/boats/${id}/category-subgroups`, { category_subgroups: subgroups }),
  updateBoatShoppingCategoryOrder: (id, order) => axios.put(`${API}/boats/${id}/shopping-category-order`, { category_order: order }),
  updateBoatBakingCategories: (id, categories) => axios.put(`${API}/boats/${id}/baking-categories`, { baking_categories: categories }),
  getActiveBoat: () => axios.get(`${API}/boats/active`),
  setPreferredShift: (shift) => axios.put(`${API}/auth/preferred-shift`, { shift }),
  movePantryLocationEntries: (fromId, toId) => axios.post(`${API}/pantry/move-location-entries`, { from_location_id: fromId, to_location_id: toId }),
  removeEmptyPantryItems: () => axios.post(`${API}/pantry/remove-empty`),
  standardizePantryNames: (dryRun) => axios.post(`${API}/pantry/standardize-names`, { dry_run: dryRun !== false }),
  applyPantryRenames: (renames) => axios.post(`${API}/pantry/apply-renames`, { renames }),
  getReceiptExtrasSummary: () => axios.get(`${API}/receipt-extras/summary`),
  recomputeReceiptExtras: (id) => axios.post(`${API}/receipts/${id}/recompute-extras`),
  setPreferredTrip: (boatId, shift, trip) => axios.put(`${API}/auth/preferred-trip`, { boat_id: boatId, shift, trip }),
  moveMealPlansShift: (fromShift, toShift, startDate, endDate) => axios.post(`${API}/meal-plans/move-shift`, { from_shift: fromShift, to_shift: toShift, start_date: startDate || "", end_date: endDate || "" }),
  getMe: () => axios.get(`${API}/auth/me`),
  onboardUser: (data) => axios.post(`${API}/auth/onboard`, data),
  uploadBoatImage: (id, formData) => axios.post(`${API}/boats/${id}/image`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  // Duplicate detection
  getPantryDuplicates: () => axios.get(`${API}/pantry/duplicates`),
  aiFindPantryDuplicates: () => axios.post(`${API}/pantry/ai-find-duplicates`),
  mergePantryDuplicates: (keys, itemIds, nameOverrides, categoryOverrides) => {
    const body = itemIds ? { item_ids: itemIds } : { keys: keys || [] };
    if (nameOverrides) body.name_overrides = nameOverrides;
    if (categoryOverrides) body.category_overrides = categoryOverrides;
    return axios.post(`${API}/pantry/merge-duplicates`, body);
  },
  // Reject a duplicate pair so the AI stops suggesting it for this boat/user
  rejectPantryDuplicate: (names, scope = "boat") => axios.post(`${API}/pantry/reject-duplicate`, { names, scope }),
  // Lærer AI ein kategori-preferanse for ein pantry-vare
  learnPantryCategory: (name, category, scope = "boat") => axios.post(`${API}/pantry/learn-category`, { name, category, scope }),
  aiFindShoppingDuplicates: (weekStart) => axios.post(`${API}/shopping-lists/${weekStart}/ai-find-duplicates`),
  // Reject a duplicate pair on the shopping list (AI learning).
  rejectShoppingDuplicate: (names, scope = "boat") => axios.post(`${API}/shopping-lists/reject-duplicate`, { names, scope }),
  // Lærer AI ein kategori-preferanse for ei bestemt vare (boat-scope default)
  learnShoppingCategory: (name, category, scope = "boat") => axios.post(`${API}/shopping-lists/learn-category`, { name, category, scope }),
  // Multi-list management
  createShoppingList: (name) => axios.post(`${API}/shopping-lists`, { name }),
  renameShoppingList: (listId, name) => axios.put(`${API}/shopping-lists/by-id/${listId}/rename`, { name }),
  deleteShoppingList: (listId) => axios.delete(`${API}/shopping-lists/by-id/${listId}`),
  updateMemberRole: (boatId, memberId, data) => axios.put(`${API}/boats/${boatId}/members/${memberId}/role`, data),
  suggestRecipeTags: (recipeId) => axios.post(`${API}/recipes/${recipeId}/suggest-tags`),
  // AI-foreslår middagar du kan lage no, basert på beholdning + utløpsdatoar
  suggestRecipesFromPantry: (body = {}) => axios.post(`${API}/recipes/suggest-from-pantry`, body),
  searchRecipeFromSource: (source, query) => axios.post(`${API}/recipes/search-source`, { source, query }),
  bulkImportPopularRecipes: (limit = 20) => axios.post(`${API}/recipes/bulk-import-popular`, { limit }, { timeout: 600000 }),
  getRecipeAutoImportConfig: () => axios.get(`${API}/admin/recipe-autoimport`),
  setRecipeAutoImportConfig: (data) => axios.put(`${API}/admin/recipe-autoimport`, data),
  updateMemberAccess: (boatId, memberId, data) => axios.put(`${API}/boats/${boatId}/members/${memberId}/access`, data),
  removeMember: (boatId, memberId) => axios.delete(`${API}/boats/${boatId}/members/${memberId}`),
  adminCreateMember: (boatId, data) => axios.post(`${API}/boats/${boatId}/members/create`, data),
  adminSetMemberPassword: (boatId, memberId, data) => axios.put(`${API}/boats/${boatId}/members/${memberId}/password`, data),
  adminSetMemberUsername: (boatId, memberId, data) => axios.put(`${API}/boats/${boatId}/members/${memberId}/username`, data),
  exportMembersCsv: (boatId) => axios.get(`${API}/boats/${boatId}/members/csv`, { responseType: "blob" }),
  updateStorageLocation: (id, data) => axios.put(`${API}/storage-locations/${id}`, data),
  reorderStorageLocations: (ids) => axios.put(`${API}/storage-locations/reorder`, { ordered_ids: ids }),
  deleteAllPantry: (code) => axios.delete(`${API}/pantry/all`, { data: { code } }),
  autoSortPantry: () => axios.post(`${API}/pantry/auto-sort`),
  // Pantry verification (last-counted)
  verifyPantryItem: (itemId) => axios.post(`${API}/pantry/items/${itemId}/verify`),
  bulkVerifyPantry: (body) => axios.post(`${API}/pantry/bulk-verify`, body),
  getPantryVerificationStatus: () => axios.get(`${API}/pantry/verification-status`),
  // Product usage statistikk
  getProductUsage: (q, startDate, endDate, comparePrev = false) => axios.get(`${API}/receipts/product-usage`, {
    params: { q, start_date: startDate, end_date: endDate, compare_prev: comparePrev },
  }),
  addPantryToShopping: (itemId) => axios.post(`${API}/pantry/${itemId}/add-to-shopping`),
  getPantryNameSuggestions: (q = "") => axios.get(`${API}/pantry/name-suggestions`, { params: q ? { q } : {} }),
  addPantryLocationEntry: (itemId, data) => axios.post(`${API}/pantry/${itemId}/locations`, data),
  updatePantryLocationEntry: (itemId, entryId, data) => axios.put(`${API}/pantry/${itemId}/locations/${entryId}`, data),
  deletePantryLocationEntry: (itemId, entryId) => axios.delete(`${API}/pantry/${itemId}/locations/${entryId}`),
  // Pantry trash (30-day soft-delete recovery)
  listPantryTrash: () => axios.get(`${API}/pantry/trash`),
  restorePantryTrashItem: (itemId) => axios.post(`${API}/pantry/trash/${itemId}/restore`),
  purgePantryTrashItem: (itemId) => axios.delete(`${API}/pantry/trash/${itemId}`),
  restoreAllPantryTrash: () => axios.post(`${API}/pantry/restore-trash`),
  emptyPantryTrash: () => axios.delete(`${API}/pantry/trash`),
  classifyShoppingItems: (names) => axios.post(`${API}/shopping-lists/classify`, { names }),
  getBoatMessages: (boatId) => axios.get(`${API}/boats/${boatId}/messages`),
  sendBoatMessage: (boatId, data) => axios.post(`${API}/boats/${boatId}/messages`, data),
  uploadMessageImage: (boatId, formData) => axios.post(`${API}/boats/${boatId}/messages/image`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }),
  deleteBoatMessage: (boatId, msgId) => axios.delete(`${API}/boats/${boatId}/messages/${msgId}`),
  getPendingChanges: (boatId) => axios.get(`${API}/boats/${boatId}/pending`),
  approveChange: (boatId, changeId) => axios.put(`${API}/boats/${boatId}/pending/${changeId}/approve`),
  rejectChange: (boatId, changeId) => axios.put(`${API}/boats/${boatId}/pending/${changeId}/reject`),

  // Trips
  getTrips: () => axios.get(`${API}/trips`),
  createTrip: (data) => axios.post(`${API}/trips`, data),
  updateTrip: (id, data) => axios.put(`${API}/trips/${id}`, data),
  deleteTrip: (id) => axios.delete(`${API}/trips/${id}`),
  activateTrip: (id) => axios.put(`${API}/trips/${id}/activate`),
  getActiveTrip: () => axios.get(`${API}/trips/active`),
  getTripBudget: (id) => axios.get(`${API}/trips/${id}/budget`),
  exportTripBudgetPdf: (id) => axios.get(`${API}/trips/${id}/budget/pdf`, { responseType: "blob" }),
  getTripHistory: (id) => axios.get(`${API}/trips/${id}/history`),

  // Receipts
  getReceipts: (tripId) => {
    const params = tripId ? { trip_id: tripId } : {};
    return axios.get(`${API}/receipts`, { params });
  },
  scanReceipt: (formData) => axios.post(`${API}/receipts/scan`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  }),
  scanReceiptMulti: (formData) => axios.post(`${API}/receipts/scan-multi`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  }),
  appendScanToReceipt: (id, formData) => axios.post(`${API}/receipts/${id}/append-scan`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  }),
  addManualReceipt: (data) => axios.post(`${API}/receipts/manual`, data),
  updateReceipt: (id, data) => axios.put(`${API}/receipts/${id}`, data),
  confirmReceipt: (id) => axios.put(`${API}/receipts/${id}/confirm`),
  deleteReceipt: (id) => axios.delete(`${API}/receipts/${id}`),

  // Feedback (issue reports + improvement suggestions)
  submitFeedback: (data) => axios.post(`${API}/feedback`, data),
  listFeedback: (params = {}) => axios.get(`${API}/admin/feedback`, { params }),
  updateFeedback: (id, data) => axios.put(`${API}/admin/feedback/${id}`, data),
};

export const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
};

export const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
export const DAY_LABELS = ["Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag", "Søndag"];
export const MEALS = ["breakfast", "lunch", "dinner", "dessert"];
export const MEAL_LABELS = { breakfast: "Frokost", lunch: "Lunsj", dinner: "Middag", dessert: "Dessert" };
